import React, { useMemo } from "react"

interface Segment {
  start: number
  end: number
}

interface DualSegmentTimelineProps {
  /** Total duration in seconds. If not provided, derived from segment ends. */
  durationSeconds?: number
  /** Segments from the metric's subvalues_by_timestamp (e.g. volume_pitch_misalignment). */
  metricSegments: Segment[]
  /** Segments from the annotation's ground_truth_subvalues_by_timestamp. */
  humanSegments: Segment[]
  /** Segments from the test experiment result. When provided, renders a third "Test" row. */
  testSegments?: Segment[]
  /** Height of each timeline row in pixels. */
  rowHeight?: number
  /** When true, shows "Not evaluated" text in the metric row instead of segments. */
  metricUnavailable?: boolean
  /** When true, shows "Not evaluated" text in the test row instead of segments. */
  testUnavailable?: boolean
  /** When true, shows a loading shimmer in the test row. */
  isEvaluating?: boolean
  /** Optional class name for the container. */
  className?: string
  /** Optional content rendered between the metric and human rows. */
  middleContent?: React.ReactNode
}

// Match human review / volume-pitch segment colors: red (metric), amber (test), purple (human)
const METRIC_ROW_COLOR = "rgba(255, 0, 0, 0.7)"
const TEST_ROW_COLOR = "rgba(245, 158, 11, 0.7)"
const HUMAN_ROW_COLOR = "rgba(147, 51, 234, 0.6)"

function SegmentRow({
  label,
  segments,
  duration,
  color,
  rowHeight,
}: {
  label: string
  segments: Segment[]
  duration: number
  color: string
  rowHeight: number
}) {
  if (duration <= 0) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-coval-text-muted w-12 shrink-0">
          {label}
        </span>
        <div
          className="flex-1 rounded bg-surface-2"
          style={{ height: rowHeight }}
        />
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-coval-text-muted w-12 shrink-0">
        {label}
      </span>
      <div
        className="flex-1 relative rounded bg-surface-2 overflow-hidden"
        style={{ height: rowHeight }}
      >
        {segments.map((seg, idx) => {
          const left = (seg.start / duration) * 100
          const width = Math.max(((seg.end - seg.start) / duration) * 100, 0.5)
          return (
            <div
              key={`${seg.start}-${seg.end}-${idx}`}
              className="absolute top-0 bottom-0 rounded-sm"
              style={{
                left: `${left}%`,
                width: `${width}%`,
                backgroundColor: color,
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

/**
 * Renders two stacked timeline rows: one for the metric's segments
 * (subvalues_by_timestamp) and one for the human review's segments
 * (ground_truth_subvalues_by_timestamp). Used on the metrics review
 * table for volume-pitch metrics.
 */
export const DualSegmentTimeline: React.FC<DualSegmentTimelineProps> = ({
  durationSeconds,
  metricSegments,
  humanSegments,
  testSegments,
  rowHeight = 14,
  metricUnavailable = false,
  testUnavailable = false,
  isEvaluating = false,
  className = "",
  middleContent,
}) => {
  const showTestRow = isEvaluating || testUnavailable || testSegments != null

  const duration = useMemo(() => {
    if (durationSeconds != null && durationSeconds > 0) {
      return durationSeconds
    }
    const allEnds = [
      ...metricSegments.map((s) => s.end),
      ...humanSegments.map((s) => s.end),
      ...(testSegments ?? []).map((s) => s.end),
    ]
    if (allEnds.length === 0) {
      return 60
    }
    return Math.max(...allEnds, 1)
  }, [durationSeconds, metricSegments, humanSegments, testSegments])

  const renderTestRow = () => {
    if (isEvaluating) {
      return (
        <div className="flex items-center gap-2">
          <span className="text-xs text-coval-text-muted w-12 shrink-0">
            Test
          </span>
          <div
            className="flex-1 rounded bg-surface-2 overflow-hidden animate-pulse"
            style={{ height: rowHeight }}
          />
        </div>
      )
    }
    if (testUnavailable) {
      return (
        <div className="flex items-center gap-2">
          <span className="text-xs text-coval-text-muted w-12 shrink-0">
            Test
          </span>
          <span className="text-xs text-coval-text-muted italic">
            Not evaluated
          </span>
        </div>
      )
    }
    return (
      <SegmentRow
        label="Test"
        segments={testSegments ?? []}
        duration={duration}
        color={TEST_ROW_COLOR}
        rowHeight={rowHeight}
      />
    )
  }

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {metricUnavailable ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-coval-text-muted w-12 shrink-0">
            Metric
          </span>
          <span className="text-xs text-coval-text-muted italic">
            Not evaluated
          </span>
        </div>
      ) : (
        <SegmentRow
          label="Metric"
          segments={metricSegments}
          duration={duration}
          color={METRIC_ROW_COLOR}
          rowHeight={rowHeight}
        />
      )}
      {showTestRow && renderTestRow()}
      {middleContent}
      <SegmentRow
        label="Human"
        segments={humanSegments}
        duration={duration}
        color={HUMAN_ROW_COLOR}
        rowHeight={rowHeight}
      />
    </div>
  )
}
